import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { weightApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { useCurrentUser } from '../auth/AuthContext.js';
import { WeightChart, WeightChartLegend } from '../components/WeightChart.js';
import { WeightTrendSummary } from '../components/WeightTrendSummary.js';
import { TrashIcon } from '../components/icons.js';
import { addDays, formatNumber, formatTime, todayInTimeZone } from '../lib/format.js';

const RANGES = [
  { days: 30, label: '30 T' },
  { days: 90, label: '90 T' },
  { days: 365, label: '1 J' },
] as const;

export function WeightPage(): JSX.Element {
  const user = useCurrentUser();
  const timeZone = user.preferences.timezone;
  const queryClient = useQueryClient();

  const [rangeDays, setRangeDays] = useState<number>(30);
  const [weightInput, setWeightInput] = useState('');
  const [bodyFatInput, setBodyFatInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const trackBodyFat = user.preferences.trackBodyFat ?? false;

  const from = addDays(todayInTimeZone(timeZone), -rangeDays);

  const seriesQuery = useQuery({
    queryKey: ['weights', 'series', rangeDays],
    queryFn: () => weightApi.series({ from: `${from}T00:00:00.000Z`, trendWindow: 7 }),
  });

  const listQuery = useQuery({
    queryKey: ['weights', 'list'],
    queryFn: () => weightApi.list({ limit: 25 }),
  });

  const invalidate = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: ['weights'] }).then(() => undefined);

  const addWeight = useMutation({
    mutationFn: (input: { weightKg: number; bodyFatPercent: number | null }) =>
      weightApi.create(input),
    onSuccess: async () => {
      setWeightInput('');
      setBodyFatInput('');
      setError(null);
      await invalidate();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Speichern fehlgeschlagen.'),
  });

  const removeWeight = useMutation({
    mutationFn: (id: string) => weightApi.remove(id),
    onSuccess: invalidate,
  });

  function submit(): void {
    const parsed = Number(weightInput.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Bitte gib ein gültiges Gewicht ein.');
      return;
    }

    // The body-fat field stays optional even while the feature is on: not every
    // weigh-in happens on the scale that measures it.
    const raw = bodyFatInput.trim();
    let bodyFatPercent: number | null = null;

    if (trackBodyFat && raw.length > 0) {
      const percent = Number(raw.replace(',', '.'));
      if (!Number.isFinite(percent) || percent < 3 || percent > 70) {
        setError('Bitte gib einen Körperfettanteil zwischen 3 und 70 % ein.');
        return;
      }
      bodyFatPercent = percent;
    }

    addWeight.mutate({ weightKg: parsed, bodyFatPercent });
  }

  const series = seriesQuery.data;
  const latest = series?.latest;

  const bodyFatDays = (series?.days ?? []).filter((day) => day.bodyFatAverage != null);
  // The newest reading that actually carried a measurement — the latest weigh-in
  // may well have been on a scale that does not measure body fat.
  const latestBodyFat = bodyFatDays.at(-1)?.bodyFatAverage ?? null;
  const latestLeanKg =
    latestBodyFat != null && latest ? latest.weightKg * (1 - latestBodyFat / 100) : null;

  return (
    <div className="stack">
      <h1>Gewicht</h1>

      <div className="card stack">
        <div className="card-title">Neue Messung</div>
        <div className="row">
          <input
            className="input grow"
            inputMode="decimal"
            placeholder="z. B. 82,4"
            value={weightInput}
            onChange={(event) => setWeightInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
          />
          <span className="hint">kg</span>
          {!trackBodyFat && (
            <button className="btn btn-primary" disabled={addWeight.isPending} onClick={submit}>
              {addWeight.isPending ? <span className="spinner" /> : 'Eintragen'}
            </button>
          )}
        </div>

        {trackBodyFat && (
          <div className="row">
            <input
              className="input grow"
              inputMode="decimal"
              placeholder="Körperfett, z. B. 18,2 (optional)"
              value={bodyFatInput}
              onChange={(event) => setBodyFatInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') submit();
              }}
            />
            <span className="hint">%</span>
            <button className="btn btn-primary" disabled={addWeight.isPending} onClick={submit}>
              {addWeight.isPending ? <span className="spinner" /> : 'Eintragen'}
            </button>
          </div>
        )}

        <span className="hint">
          Mehrere Messungen pro Tag sind ausdrücklich vorgesehen — der Trend mittelt sie.
        </span>
        {error && (
          <div className="banner banner-error" role="alert">
            {error}
          </div>
        )}
      </div>

      <div className="card stack">
        <div className="row-between">
          <div>
            <div className="calorie-headline">
              <span className="value">{latest ? formatNumber(latest.weightKg, 1) : '—'}</span>
              <span className="unit">kg</span>
            </div>
            {series?.changeKg != null && (
              <span className="hint">
                {series.changeKg >= 0 ? '+' : '−'}
                {formatNumber(Math.abs(series.changeKg), 2)} kg im Zeitraum (Trend)
              </span>
            )}
            {series && (
              <div style={{ marginTop: 8 }}>
                <WeightTrendSummary series={series} />
              </div>
            )}
          </div>

          <div className="segmented" style={{ gridAutoFlow: 'column' }}>
            {RANGES.map((range) => (
              <button
                key={range.days}
                className={rangeDays === range.days ? 'active' : ''}
                onClick={() => setRangeDays(range.days)}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        {seriesQuery.isPending ? (
          <div className="empty-state">
            <div className="spinner" style={{ margin: '0 auto' }} role="status" aria-label="Lädt" />
          </div>
        ) : (
          <>
            <WeightChart days={series?.days ?? []} />
            {(series?.days.length ?? 0) > 1 && <WeightChartLegend />}
          </>
        )}
      </div>

      {trackBodyFat && (
        <div className="card stack">
          <div className="row-between">
            <div>
              <div className="calorie-headline">
                <span className="value">
                  {latestBodyFat != null ? formatNumber(latestBodyFat, 1) : '—'}
                </span>
                <span className="unit">% Körperfett</span>
              </div>
              {latestBodyFat != null && latestLeanKg != null && (
                <span className="hint">
                  {formatNumber(latestLeanKg, 1)} kg fettfreie Masse ·{' '}
                  {formatNumber((latestBodyFat / 100) * (latest?.weightKg ?? 0), 1)} kg Fett
                </span>
              )}
            </div>
          </div>

          {bodyFatDays.length === 0 ? (
            <div className="empty-state">
              Noch keine Körperfettwerte. Trage oben einen Prozentwert mit ein.
            </div>
          ) : (
            <>
              <WeightChart days={series?.days ?? []} metric="bodyFat" />
              {bodyFatDays.length > 1 && <WeightChartLegend />}
              <span className="hint">
                Werte von Körperfettwaagen schwanken stark mit dem Wasserhaushalt — der Trend ist
                aussagekräftiger als die Einzelmessung.
              </span>
            </>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-title">Letzte Messungen</div>

        {listQuery.data?.entries.length === 0 && (
          <div className="empty-state">Noch nichts erfasst.</div>
        )}

        <div className="entry-list">
          {listQuery.data?.entries.map((entry) => (
            <div className="entry" key={entry.id}>
              <div className="grow">
                <div className="entry-name">
                  {formatNumber(entry.weightKg, 1)} kg
                  {/* Shown whenever the reading has one, even with the feature
                      switched off — hiding a recorded value would look like
                      data loss. */}
                  {entry.bodyFatPercent != null && (
                    <span className="hint" style={{ marginLeft: 8 }}>
                      {formatNumber(entry.bodyFatPercent, 1)} % KFA
                    </span>
                  )}
                </div>
                <div className="entry-meta">
                  {new Date(entry.measuredAt).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    timeZone,
                  })}{' '}
                  · {formatTime(entry.measuredAt, timeZone)}
                </div>
              </div>

              <button
                className="icon-btn"
                aria-label="Messung löschen"
                disabled={removeWeight.isPending}
                onClick={() => removeWeight.mutate(entry.id)}
              >
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
